/**
 * The store-features bar's marquee (`sections/okt-store-features.liquid`; THEME.md §7.3; D-84 M2; T-108).
 *
 * Wraps Horizon's `marquee-component` (assets/marquee.js) without changing it. Horizon only slows the
 * marquee on hover; this adds the WCAG 2.2.2 controls:
 * - The button pauses and plays it (`data-paused`). Its label says what it will do; its icon swaps by CSS.
 * - A touch or pen pauses it while it holds the marquee (`data-touched`).
 * - Keyboard focus inside the marquee pauses it while it's there (CSS, `:focus-within`).
 * The pause itself is CSS: `animation-play-state: paused` on Horizon's `.marquee__wrapper`, which keeps
 * Horizon's own playback rate (its hover slow-down) intact.
 * - Horizon's copy of the content for the loop is `aria-hidden`; it also becomes `inert`, so links in it
 *   never take keyboard focus. The blocks Horizon repeats to fill a wide marquee get the same, so screen
 *   readers read each block once.
 * Under reduced motion Horizon's marquee doesn't move and the button is hidden (CSS).
 */
class OktMarquee extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  #copies = new MutationObserver(() => this.#hideCopies());

  connectedCallback() {
    this.#button = this.querySelector('.okt-store-features__toggle');
    this.#button?.addEventListener('click', this.#onToggle);
    this.addEventListener('pointerdown', this.#onPointerDown);
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) this.addEventListener(type, this.#onPointerEnd);

    const wrapper = this.querySelector('.marquee__wrapper');
    const content = wrapper?.querySelector(':scope > .marquee__content');
    if (wrapper) this.#copies.observe(wrapper, { childList: true });
    if (content) this.#copies.observe(content, { childList: true });
    this.#hideCopies();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onToggle);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) this.removeEventListener(type, this.#onPointerEnd);
    this.#copies.disconnect();
  }

  #onToggle = () => {
    const button = this.#button;
    if (!button) return;
    const paused = !this.hasAttribute('data-paused');
    this.toggleAttribute('data-paused', paused);
    const label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    if (event.target instanceof Node && this.#button?.contains(event.target)) return;
    this.setAttribute('data-touched', '');
  };

  /** @param {PointerEvent} event */
  #onPointerEnd = (event) => {
    if (event.pointerType === 'mouse') return;
    this.removeAttribute('data-touched');
  };

  #hideCopies() {
    const copies = this.querySelectorAll(
      '.marquee__wrapper > [aria-hidden="true"], .marquee__content > .marquee__repeated-items:not(:first-child)'
    );
    for (const copy of copies) {
      if (copy.getAttribute('aria-hidden') !== 'true') copy.setAttribute('aria-hidden', 'true');
      if (!copy.inert) copy.inert = true;
    }
  }
}

if (!customElements.get('okt-marquee')) {
  customElements.define('okt-marquee', OktMarquee);
}
